import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Input, InputNumber, Select, Button, Upload, message, Radio, Spin, Empty } from "antd";
import { InboxOutlined, SearchOutlined } from "@ant-design/icons";
import { searchProducts } from "../../services/productService";
import { UNIT_CODE_OPTIONS } from "../../constants/invoiceConstants";

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

const CreateOrderItemModal = ({
  show,
  onClose,
  item,
  onChange,
  onSubmit,
  employees,
  loadingEmployees
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [inputMode, setInputMode] = useState("manual"); // "manual" or "inventory"
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const debounceRef = useRef(null);

  // Debounced search function
  const handleSearch = (searchText) => {
    setSearchValue(searchText);

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchText || searchText.length < 1) {
      setProducts([]);
      return;
    }

    setLoadingProducts(true);

    // Debounce: wait 400ms before searching
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await searchProducts(searchText, 0, 6);
        setProducts(response.data.content || []);
      } catch (error) {
        console.error("Ürün arama hatası:", error);
        setProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    }, 400);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setInputMode("manual");
      setSelectedProduct(null);
      setSearchValue("");
      setProducts([]);
    }
  }, [show]);

  const handleInputChange = (name, value) => {
    onChange({
      target: {
        name,
        value,
      },
    });
  };

  const handleInputModeChange = (e) => {
    const mode = e.target.value;
    setInputMode(mode);
    setSelectedProduct(null);
    setSearchValue("");
    setProducts([]);
    // Reset item name and price when switching modes
    handleInputChange("itemName", "");
    handleInputChange("unitPrice", null);
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setSelectedProduct(product);
      handleInputChange("itemName", product.name);
      handleInputChange("unitPrice", product.price);
      handleInputChange("taxRate", product.taxRate);
      handleInputChange("unitCode", product.unitCode);
    }
  };

  const handleFileChange = ({ fileList }) => {
    const newFileList = fileList.slice(-1);
    setFileList(newFileList);
    
    if (newFileList.length > 0) {
      const file = newFileList[0].originFileObj;
      handleInputChange("file", file);
    } else {
      handleInputChange("file", null);
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      await onSubmit(fileList.length > 0 ? fileList[0].originFileObj : null);
      setFileList([]);
    } catch (error) {
      console.error("Sipariş kalemi oluştururken hata meydana geldi:", error);
    } finally {
      setUploading(false);
    }
  };

  const beforeUpload = (file) => {
    const isPDF = file.type === 'application/pdf';
    if (!isPDF) {
      message.error('Yalnızca PDF dosyası yükleyebilirsin');
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Dosya boyutu en fazla 10MB olmalıdır');
    }
    
    return isPDF && isLt10M;
  };

  return (
    <Modal
      title="Sipariş Kalemi Ekle"
      open={show}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          İptal
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={uploading} 
          onClick={handleSubmit}
        >
          Ekle
        </Button>
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Ürün Giriş Yöntemi">
          <Radio.Group value={inputMode} onChange={handleInputModeChange}>
            <Radio.Button value="manual">Manuel Giriş</Radio.Button>
            <Radio.Button value="inventory">Envanterden Seç</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {inputMode === "inventory" && (
          <Form.Item label="Ürün Seç" required>
            <Select
              showSearch
              placeholder="Ürün adı yazarak arayın..."
              value={selectedProduct?.id}
              onSearch={handleSearch}
              onChange={handleProductSelect}
              filterOption={false}
              notFoundContent={
                loadingProducts ? (
                  <Spin size="small" />
                ) : searchValue ? (
                  <Empty description="Ürün bulunamadı" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <span style={{ color: "#999" }}>Aramak için yazmaya başlayın</span>
                )
              }
              style={{ width: "100%" }}
              suffixIcon={<SearchOutlined />}
            >
              {products.map((product) => (
                <Option key={product.id} value={product.id}>
                  {product.name} - ₺{product.price}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {inputMode === "manual" && (
          <Form.Item label="Kalem İsmi" required>
            <Input
              name="itemName"
              value={item.itemName}
              onChange={(e) => onChange(e)}
              placeholder="Kalem ismini giriniz"
            />
          </Form.Item>
        )}
        <Form.Item label="Adet" required>
          <InputNumber
            name="quantity"
            value={item.quantity}
            onChange={(value) => handleInputChange("quantity", value)}
            min={1}
            style={{ width: "100%" }}
          />
        </Form.Item>
        {inputMode === "manual" && (
          <Form.Item label="Birim Fiyat" required>
            <InputNumber
              name="unitPrice"
              value={item.unitPrice}
              onChange={(value) => handleInputChange("unitPrice", value)}
              min={0}
              step={0.01}
              formatter={(value) => `₺ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/₺\s?|(,*)/g, "")}
              style={{ width: "100%" }}
            />
          </Form.Item>
        )}
        <Form.Item label="KDV Oranı (%)">
          <InputNumber
            name="taxRate"
            value={item.taxRate}
            onChange={(value) => handleInputChange("taxRate", value)}
            min={0}
            max={100}
            step={1}
            addonAfter="%"
            style={{ width: "100%" }}
            placeholder="18"
          />
        </Form.Item>
        <Form.Item label="Birim">
          <Select
            value={item.unitCode}
            onChange={(value) => handleInputChange("unitCode", value)}
            placeholder="Birim seçiniz"
            allowClear
            options={UNIT_CODE_OPTIONS}
          />
        </Form.Item>
        <Form.Item label="Sorumlu Personel">
          <Select
            loading={loadingEmployees}
            name="assignedEmployeeId"
            value={item.assignedEmployeeId}
            onChange={(value) => handleInputChange("assignedEmployeeId", value)}
            placeholder="Personel seçiniz"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            allowClear
          >
            {employees.map((employee) => (
              <Option
                key={employee.id}
                value={employee.id}
                label={`${employee.name} ${employee.surname}`}
              >
                {employee.name} {employee.surname}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Notlar">
          <TextArea
            name="notes"
            value={item.notes}
            onChange={(e) => onChange(e)}
            placeholder="Notlarınızı ekleyin"
            rows={4}
          />
        </Form.Item>

        <Form.Item label="PDF Dosya">
          <Dragger
            name="file"
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={beforeUpload}
            maxCount={1}
            accept=".pdf"
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">PDF dosyası yüklemek için tıklayın veya sürükleyin</p>
            <p className="ant-upload-hint">Sadece PDF dosyaları desteklenir (max. 10MB)</p>
          </Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateOrderItemModal;